import { useMemo, useState, useRef } from 'react';
import { Search, ChevronDown } from 'lucide-react';
import {
  Input,
  Checkbox,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  DatePicker,
} from '@roger/ui-components';
import { useVirtualizer } from '@tanstack/react-virtual';

interface BranchSelectorProps {
  options: { value: string; label: string }[];
  value: string[];
  onChange: (value: string[]) => void;
}

const BranchSelector = ({ options, value, onChange }: BranchSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const parentRef = useRef<HTMLDivElement>(null);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    return options.filter(opt =>
      opt.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [options, search]);

  const allSelected = options.length > 0 && value.length === options.length;

  const handleAllChange = (_checked: boolean) => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(options.map(o => o.value));
    }
  };

  const handleOptionChange = (id: string, checked: boolean) => {
    if (checked) {
      onChange([...value, id]);
    } else {
      onChange(value.filter(v => v !== id));
    }
  };

  const label = allSelected
    ? 'All branches selected'
    : value.length === 0
      ? 'Select branches'
      : `${value.length} branches selected`;

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 32,
    overscan: 5,
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className='w-[250px] justify-between font-normal'
        >
          <span className='truncate'>{label}</span>
          <ChevronDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[250px] p-2' align='start'>
        <div className='relative mb-2'>
          <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground opacity-50' />
          <Input
            className='pl-8 h-9'
            placeholder='Search...'
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
        </div>

        <div className='px-2 py-1.5 mb-1'>
          <div className='flex items-center space-x-2'>
            <Checkbox
              id='select-all-branches'
              checked={allSelected}
              onCheckedChange={checked => handleAllChange(!!checked)}
            />
            <label
              htmlFor='select-all-branches'
              className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer'
            >
              All branches
            </label>
          </div>
        </div>

        <div ref={parentRef} className='h-[200px] overflow-y-auto w-full'>
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {filteredOptions.length > 0 ? (
              virtualizer.getVirtualItems().map(virtualItem => {
                const opt = filteredOptions[virtualItem.index];
                const isSelected = value.includes(opt.value);
                return (
                  <div
                    key={opt.value}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualItem.size}px`,
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                    className='flex items-center space-x-2 px-2 py-1.5'
                  >
                    <Checkbox
                      id={`branch-${opt.value}`}
                      checked={isSelected}
                      onCheckedChange={checked =>
                        handleOptionChange(opt.value, !!checked)
                      }
                    />
                    <label
                      htmlFor={`branch-${opt.value}`}
                      className='text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer truncate'
                      title={opt.label}
                    >
                      {opt.label}
                    </label>
                  </div>
                );
              })
            ) : (
              <div className='py-6 text-center text-sm text-muted-foreground'>
                No branches found
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export interface VisitCompletionByClinicianAndBranchReportHeaderProps {
  branchOptions: { value: string; label: string }[];
  selectedBranches: string[];
  onSelectedBranchesChange: (value: string[]) => void;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  fromDate: Date | null;
  toDate: Date | null;
  onFromDateChange: (value: Date | null) => void;
  onToDateChange: (value: Date | null) => void;
  showBreakdown: boolean;
  onShowBreakdownChange: (value: boolean) => void;
  hideNeverLoggedIn: boolean;
  onHideNeverLoggedInChange: (value: boolean) => void;
}

const VisitCompletionByClinicianAndBranchReportHeader = ({
  branchOptions,
  selectedBranches,
  onSelectedBranchesChange,
  searchTerm,
  onSearchTermChange,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  showBreakdown,
  onShowBreakdownChange,
  hideNeverLoggedIn,
  onHideNeverLoggedInChange,
}: VisitCompletionByClinicianAndBranchReportHeaderProps) => (
  <>
    <div className='flex gap-4 items-center mb-4 mt-[-8px]'>
      <BranchSelector
        options={branchOptions}
        value={selectedBranches}
        onChange={onSelectedBranchesChange}
      />
      <div className='text-sm text-[#735bd6]'>
        All data is from 12/02/2025 onward
      </div>
    </div>

    <div className='mb-4'>
      <div className='flex gap-4 items-center flex-wrap'>
        <div className='relative w-[250px]'>
          <Search className='absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
          <Input
            type='text'
            placeholder='Search by clinician name...'
            value={searchTerm}
            onChange={e => onSearchTermChange(e.target.value)}
            className='pl-9'
          />
        </div>

        <div className='w-40'>
          <label
            htmlFor='fromDateBranch'
            className='text-sm font-medium mb-1.5 block'
          >
            From Date
          </label>
          <DatePicker
            className='w-full'
            typable
            showClearButton
            date={fromDate ?? undefined}
            onDateChange={d => onFromDateChange(d ?? null)}
          />
        </div>

        <div className='w-40'>
          <label
            htmlFor='toDateBranch'
            className='text-sm font-medium mb-1.5 block'
          >
            To Date
          </label>
          <DatePicker
            className='w-full'
            typable
            showClearButton
            date={toDate ?? undefined}
            onDateChange={d => onToDateChange(d ?? null)}
          />
        </div>

        <div className='flex items-center gap-4 ml-2'>
          <div className='flex items-center space-x-2'>
            <Checkbox
              id='show-breakdown'
              checked={showBreakdown}
              onCheckedChange={checked => {
                const v = !!checked;
                onShowBreakdownChange(v);
                localStorage.setItem(
                  'adoptionReportBranch_showBreakdown',
                  JSON.stringify(v)
                );
              }}
            />
            <label htmlFor='show-breakdown' className='text-sm cursor-pointer'>
              Visit Type breakdown
            </label>
          </div>

          <div className='flex items-center space-x-2'>
            <Checkbox
              id='hide-never-logged-in'
              checked={hideNeverLoggedIn}
              onCheckedChange={checked => {
                const v = !!checked;
                onHideNeverLoggedInChange(v);
                localStorage.setItem(
                  'adoptionReportBranch_hideNeverLoggedIn',
                  JSON.stringify(v)
                );
              }}
            />
            <label
              htmlFor='hide-never-logged-in'
              className='text-sm cursor-pointer'
            >
              Hide users who never logged in
            </label>
          </div>
        </div>
      </div>
    </div>
  </>
);

export default VisitCompletionByClinicianAndBranchReportHeader;
